import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import Sidebar from './sidebar';

import { 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper, 
  Typography, 
  Box, 
  Chip, 
  CircularProgress, 
  Alert,
  Button,
  useMediaQuery,
  useTheme,
  IconButton,
  Card,
  CardContent,
  Grid,
  Drawer
} from '@mui/material';
import { 
  Timeline, 
  LocalActivity, 
  PeopleAlt, 
  Category,
  Download,
//   Menu as MenuIcon,
  Close as CloseIcon
} from '@mui/icons-material';

const CreatorWheels = () => {
    const [wheels, setWheels] = useState([]); 
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const location = useLocation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const creatorId = new URLSearchParams(location.search).get('key');

    useEffect(() => {
        if (creatorId) {
            fetchWheels();
        }
    }, [creatorId]);

    const fetchWheels = async () => {
        const payload = {
            apikey: process.env.REACT_APP_API_KEY,
            module: "admin",
            method: "tWheellist",
            user_id: creatorId,
        };

        try {
            const response = await axios.post(process.env.REACT_APP_API_URL, payload);
            const jsonData = JSON.parse(response.data.body);

            if (jsonData.status === "success") {
                setWheels(jsonData.data.wheels || []); 
            } else {
                setError("Failed to fetch wheels. " + jsonData.message);
            }
        } catch (err) {
            console.error("Error fetching data:", err); 
            setError("Error fetching data: " + err.message);
        } finally {
            setLoading(false);
        }
    };

    const toggleSidebar = () => {
        setSidebarOpen(!sidebarOpen);
    };

    const downloadWheelsData = () => {
        // Transform wheels data for CSV export
        const exportData = wheels.map(wheel => ({
            'Wheel Name': wheel.wheel_name,
            'Created Date': new Date(wheel.created_date).toLocaleDateString(),
            'Status': wheel.status,
            'Total Players': wheel.players_with_pies.length || 0,
            'Pie Segments': wheel.pies.join(' | '),
            'Spinning Time': new Date(wheel.spinning_time).toLocaleString(),
            'Minimum Count': wheel.minimum_count
        }));

        // Convert to CSV
        const csvContent = [
            Object.keys(exportData[0]).join(','),
            ...exportData.map(item => 
                Object.values(item).map(value => 
                    `"${String(value).replace(/"/g, '""')}"` // Escape quotes
                ).join(',')
            )
        ].join('\n');

        // Create and download CSV file
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `creator_wheels_${creatorId}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'published': return 'success';
            case 'wfa': return 'warning';
            case 'inactive': return 'error';
            default: return 'default';
        }
    };

    const renderWheelStatus = (status) => {
        const statusMap = {
            'published': 'Active',
            'wfa': 'Waiting for Approval',
            'inactive': 'Inactive'
        };
        return statusMap[status] || status;
    };

    const renderMobileWheelCard = (wheel) => (
        <Card key={wheel._id} sx={{ mb: 2, width: '100%' }}>
            <CardContent>
                <Box display="flex" alignItems="center" mb={1}>
                    <LocalActivity sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="h6" fontWeight="medium">
                        {wheel.wheel_name}
                    </Typography>
                </Box>
                
                <Grid container spacing={1}>
                    <Grid item xs={6}>
                        <Box display="flex" alignItems="center">
                            <Timeline sx={{ mr: 1, fontSize: 'small', color: 'text.secondary' }} />
                            <Typography variant="body2">
                                {new Date(wheel.created_date).toLocaleDateString()}
                            </Typography>
                        </Box>
                    </Grid>
                    
                    <Grid item xs={6}>
                        <Chip 
                            label={renderWheelStatus(wheel.status)} 
                            color={getStatusColor(wheel.status)} 
                            size="small" 
                        />
                    </Grid>
                    
                    <Grid item xs={6}>
                        <Box display="flex" alignItems="center">
                            <PeopleAlt sx={{ mr: 1, fontSize: 'small', color: 'text.secondary' }} />
                            <Typography variant="body2">
                                Players: {wheel.players_with_pies.length || 0}
                            </Typography>
                        </Box>
                    </Grid>
                    
                    <Grid item xs={12}>
                        <Box display="flex" alignItems="flex-start">
                            <Category sx={{ mr: 1, mt: 0.5, fontSize: 'small', color: 'text.secondary' }} />
                            <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                                Segments: {wheel.pies.join(', ')}
                            </Typography>
                        </Box>
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    );

    const renderDesktopTable = () => (
        <TableContainer component={Paper} elevation={3}>
            <Table>
                <TableHead>
                    <TableRow>
                        <TableCell>Wheel Name</TableCell>
                        <TableCell>Created Date</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Total Players</TableCell>
                        <TableCell>Pie Segments</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {wheels.map(wheel => (
                        <TableRow key={wheel._id} hover>
                            <TableCell>
                                <Box display="flex" alignItems="center">
                                    <LocalActivity sx={{ mr: 2, color: 'primary.main' }} />
                                    <Typography variant="body1" fontWeight="medium">
                                        {wheel.wheel_name}
                                    </Typography>
                                </Box>
                            </TableCell>
                            <TableCell>
                                <Box display="flex" alignItems="center">
                                    <Timeline sx={{ mr: 2, color: 'text.secondary' }} />
                                    {new Date(wheel.created_date).toLocaleDateString()}
                                </Box>
                            </TableCell>
                            <TableCell>
                                <Chip 
                                    label={renderWheelStatus(wheel.status)} 
                                    color={getStatusColor(wheel.status)} 
                                    size="small" 
                                />
                            </TableCell>
                            <TableCell>
                                <Box display="flex" alignItems="center">
                                    <PeopleAlt sx={{ mr: 2, color: 'text.secondary' }} />
                                    {wheel.players_with_pies.length || 0}
                                </Box>
                            </TableCell>
                            <TableCell>
                                <Box display="flex" alignItems="center">
                                    <Category sx={{ mr: 2, color: 'text.secondary' }} />
                                    {wheel.pies.join(', ')}
                                </Box>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );

    if (loading) {
        return (
            <Box 
                display="flex" 
                justifyContent="center" 
                alignItems="center" 
                height="100vh"
            >
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error">{error}</Alert>
            </Box>
        );
    }

    return (
        <Box sx={{ display: 'flex' }}>
            {isMobile ? (
                <Drawer
                    variant="temporary"
                    open={sidebarOpen}
                    onClose={toggleSidebar}
                    ModalProps={{
                        keepMounted: true, // Better mobile performance
                    }}
                    sx={{
                        '& .MuiDrawer-paper': { 
                            width: 280,
                            boxSizing: 'border-box' 
                        },
                    }}
                >
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1 }}>
                        <IconButton onClick={toggleSidebar}>
                            <CloseIcon />
                        </IconButton>
                    </Box>
                    <Sidebar />
                </Drawer>
            ) : (
                <Box component="nav" sx={{ width: { md: 280 }, flexShrink: 0 }}>
                    <Sidebar />
                </Box>
            )}
            
            <Box 
                component="main"
                sx={{ 
                    flexGrow: 1, 
                    p: { xs: 2, sm: 3 },
                    pl: { md: 3, lg: 4 },
                    width: { xs: '100%', md: `calc(100% - 280px)` }
                }}
            >
                {isMobile && (
                    <IconButton 
                        color="inherit" 
                        aria-label="open sidebar" 
                        edge="start" 
                        onClick={toggleSidebar}
                        sx={{ mb: 2 }}
                    >
                        {/* <MenuIcon /> */}
                    </IconButton>
                )}
                
                <Box 
                    display="flex" 
                    flexDirection={isMobile ? 'column' : 'row'}
                    justifyContent={isMobile ? 'flex-start' : 'space-between'}
                    alignItems={isMobile ? 'flex-start' : 'center'}
                    mb={2}
                    gap={isMobile ? 2 : 0}
                >
                    <Typography variant={isMobile ? 'h5' : 'h4'}>Creator's Wheels</Typography>
                    {wheels.length > 0 && (
                        <Button 
                            variant="contained" 
                            color="primary" 
                            startIcon={<Download />}
                            onClick={downloadWheelsData}
                            size={isMobile ? 'small' : 'medium'}
                            fullWidth={isMobile}
                        >
                            Download CSV
                        </Button>
                    )}
                </Box>
                
                {wheels.length === 0 ? (
                    <Alert severity="info">No wheels found for this creator</Alert>
                ) : (
                    <>
                        {isMobile ? (
                            <Box sx={{ width: '100%' }}>
                                {wheels.map(renderMobileWheelCard)}
                            </Box>
                        ) : (
                            renderDesktopTable()
                        )}
                    </>
                )}
            </Box>
        </Box>
    );
};

export default CreatorWheels;